import React, { ReactElement, useState } from 'react';
import { merge } from 'lodash';
import { MetricsTracker, USER_EVENTS } from '@sm/metrics';
import { t, defineMessages } from '@sm/intl';
import { Immutable } from '@wds/utils';
import { Button } from '@wds/button';
import { Box } from '@wds/box';
import { Link } from '@wds/link';
import { Sheet } from '@wds/sheet';
import { Typography } from '@wds/typography';
import { SkeletonLoader, InfiniteScrollTrigger } from '@sm/webassets';
import {
  useInProductMessagesQuery,
  InProductMessagesQuery,
} from './InProductMessages.generated';
import { useMarkAllMessagesAsReadMutation } from './MarkAllMessagesAsRead.generated';
import useStyles from './useStyles';
import NotificationItem from './NotificationItem';
import notificationZeroStateImage from '../../../static/images/notification-zero-state.svg';

const PAGE_SIZE = 10;

export const COPY = defineMessages({
  NOTIFICATION_CENTER: {
    id: 'NotificationsSheet.notificationCenter',
    defaultMessage: 'Notification center',
    description:
      '[Type: Label][Vis.: high] - This is the title of the notification center.',
  },
  NO_NOTIFICATIONS_IMG: {
    id: 'NotificationsSheet.noNotificationsImg',
    defaultMessage: 'Graphic of a notifications interface',
    description:
      '[Type: Label][Vis.: high] - This is an image depicting the notifications interface.',
  },
  NO_NOTIFICATIONS_TITLE: {
    id: 'NotificationsSheet.noNotificationsTitle',
    defaultMessage: 'Watch the bell for updates',
    description:
      '[Type: Label][Vis.: high] - This is the title displayed when there are no notifications.',
  },
  NO_NOTIFICATIONS_BODY: {
    id: 'NotificationsSheet.noNotificationsBody',
    defaultMessage:
      "We'll notify you about new activity on your surveys or account.",
    description:
      '[Type: Label][Vis.: high] - This is the message displayed when there are no notifications',
  },
  LEARN: {
    id: 'NotificationsSheet.learn',
    defaultMessage: 'Learn more',
    description:
      '[Type: Label][Vis.: high] - This is the text for a "Learn more" cta link.',
  },
  MARK_ALL: {
    id: 'NotificationsSheet.markAll',
    defaultMessage: 'Mark all as read',
    description:
      '[Type: Label][Vis.: high] - This is the label for the "Mark all as read" action button.',
  },
  MARK_ALL_ERROR: {
    id: 'NotificationsSheet.markAllError',
    defaultMessage: 'There was an error marking all notifications as read.',
    description:
      '[Type: Label][Vis.: high] - This is the error message displayed when there is an issue with marking all notifications as read.',
  },
  END_OF_NOTIFICATIONS: {
    id: 'NotificationsSheet.endOfNotifications',
    defaultMessage: 'These are all your notifications from the last 30 days.',
    description:
      '[Type: Label][Vis.: high] - This is the message indicating the end of the list of notifications.',
  },
  NOTIFICATIONS_ERROR: {
    id: 'NotificationsSheet.notificationsError',
    defaultMessage: 'An error occured while loading your notifications.',
    description:
      '[Type: Label][Vis.: high] - This is the error message displayed when there is an issue with loading notifications.',
  },
  SHARE: {
    id: 'NotificationsSheet.share',
    defaultMessage: 'Share feedback',
    description:
      '[Type: Label][Vis.: high] - This is the text for a "Share feedback" cta link thhat brings up a survey.',
  },
});

const NotificationSkeletonLoader = () => (
  <Box m={5}>
    <SkeletonLoader height={100} width="100%">
      <path
        x="16"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 5C0 2.23858 2.23858 0 5 0H81C83.7614 0 86 2.23858 86 5C86 7.76142 83.7614 10 81 10H5C2.23858 10 0 7.76142 0 5Z"
        fill="#E5E6EA"
      />
      <path
        x="16"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 26C0 23.2386 2.23858 21 5 21H282C284.761 21 287 23.2386 287 26C287 28.7614 284.761 31 282 31H5C2.23858 31 0 28.7614 0 26Z"
        fill="#B7B8BC"
      />
      <path
        x="16"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 47C0 44.2386 2.23858 42 5 42H144C146.761 42 149 44.2386 149 47C149 49.7614 146.761 52 144 52H5C2.23858 52 0 49.7614 0 47Z"
        fill="#C1C3C9"
      />
    </SkeletonLoader>
    <SkeletonLoader height={100} width="100%">
      <path
        x="16"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 5C0 2.23858 2.23858 0 5 0H81C83.7614 0 86 2.23858 86 5C86 7.76142 83.7614 10 81 10H5C2.23858 10 0 7.76142 0 5Z"
        fill="#E5E6EA"
      />
      <path
        x="16"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 26C0 23.2386 2.23858 21 5 21H282C284.761 21 287 23.2386 287 26C287 28.7614 284.761 31 282 31H5C2.23858 31 0 28.7614 0 26Z"
        fill="#B7B8BC"
      />
      <path
        x="16"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 47C0 44.2386 2.23858 42 5 42H144C146.761 42 149 44.2386 149 47C149 49.7614 146.761 52 144 52H5C2.23858 52 0 49.7614 0 47Z"
        fill="#C1C3C9"
      />
    </SkeletonLoader>
    <SkeletonLoader height={100} width="100%">
      <path
        x="16"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 5C0 2.23858 2.23858 0 5 0H81C83.7614 0 86 2.23858 86 5C86 7.76142 83.7614 10 81 10H5C2.23858 10 0 7.76142 0 5Z"
        fill="#E5E6EA"
      />
      <path
        x="16"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 26C0 23.2386 2.23858 21 5 21H282C284.761 21 287 23.2386 287 26C287 28.7614 284.761 31 282 31H5C2.23858 31 0 28.7614 0 26Z"
        fill="#B7B8BC"
      />
      <path
        x="16"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 47C0 44.2386 2.23858 42 5 42H144C146.761 42 149 44.2386 149 47C149 49.7614 146.761 52 144 52H5C2.23858 52 0 49.7614 0 47Z"
        fill="#C1C3C9"
      />
    </SkeletonLoader>
  </Box>
);

export type NotificationsSheetProps = Immutable<{
  showNotifications: boolean;
  unreadNotificationsCount: number;
  pageName?: string;
  pageURL?: string;
  userId?: string;
  packageId?: string;
  lang?: string;
}> & {
  toggleNotifications(): void;
  refetchNotificationIconCount(): void;
};

const NotificationsSheet = ({
  showNotifications,
  toggleNotifications,
  unreadNotificationsCount,
  pageName,
  pageURL,
  userId,
  packageId,
  refetchNotificationIconCount,
  lang,
}: NotificationsSheetProps): ReactElement => {
  const { notificationsContainer, notificationsList, notificationsEmpty } =
    useStyles();
  const [hasMoreToLoad, setHasMoreToLoad] = useState(true);

  const trackNotificationsEvent = (
    interactionLocation: string,
    userTotalMessages?: number
  ) => {
    MetricsTracker.track({
      name: USER_EVENTS.ELEMENT_INTERACTION,
      data: {
        amplitudeEvent: 'notification center interaction',
        interactionType: 'click',
        interactionLocation,
        messageCountTotal: userTotalMessages,
        messageCountUnread: unreadNotificationsCount,
        pageName,
        pageURL,
      },
    });
  };

  const {
    loading,
    error,
    data,
    refetch: refetchMessages,
    fetchMore: fetchMoreMessages,
  } = useInProductMessagesQuery({
    variables: {
      input: {
        isDeleted: false,
        beforeMessageId: null,
        pageSize: PAGE_SIZE,
      },
    },
    skip: !showNotifications,
    fetchPolicy: 'network-only',
  });

  const successfullyMarkedAllAsRead = () => {
    refetchMessages();
    refetchNotificationIconCount();
  };

  const [
    markAllMessagesAsRead,
    { loading: loadingMarkAll, error: errorMarkAll },
  ] = useMarkAllMessagesAsReadMutation({
    onCompleted: successfullyMarkedAllAsRead,
  });

  const notificationsContent = data?.inProductMessages?.items;
  const totalCount = data?.inProductMessages?.totalCount;
  const notificationsLastMessageId =
    data?.inProductMessages?.cursorInfo.lastMessageId;
  const userHasNoMessages = notificationsContent?.length === 0;

  const showMarkAllAsRead =
    notificationsContent?.some(message => message.isRead === false) ||
    unreadNotificationsCount > 0;

  const clickMarkAllAsRead = () => {
    markAllMessagesAsRead({
      variables: {
        input: {
          notAfterDate,
        },
      },
    });
    trackNotificationsEvent('mark all as read', totalCount);
  };

  const updateMessages = (
    prevData: InProductMessagesQuery,
    { fetchMoreResult: newData }: { fetchMoreResult?: InProductMessagesQuery }
  ) => {
    if (!newData) {
      return prevData;
    }

    if (newData?.inProductMessages.items.length < PAGE_SIZE) {
      setHasMoreToLoad(false);
    } else {
      setHasMoreToLoad(true);
    }

    const combinedData = merge({}, prevData, newData, {
      inProductMessages: {
        items: prevData.inProductMessages.items.concat(
          newData.inProductMessages.items
        ),
      },
    });

    return combinedData;
  };

  const onLoadMore = () => {
    fetchMoreMessages({
      variables: {
        input: {
          beforeMessageId: notificationsLastMessageId,
          pageSize: PAGE_SIZE,
          isDeleted: false,
        },
      },
      updateQuery: updateMessages,
    });
  };

  const onClickFeedback = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackNotificationsEvent('share feedback', totalCount);
  };

  const notAfterDate = notificationsContent && notificationsContent[0]?.sentAt;

  const closeSheet = () => {
    setHasMoreToLoad(true);
    toggleNotifications();
  };

  return (
    <Sheet
      show={showNotifications}
      onClose={closeSheet}
      showCloseButton
      offsetTop={48}
    >
      <Sheet.Header header={t(COPY.NOTIFICATION_CENTER)} />
      <Sheet.Body padding={false}>
        <Box className={notificationsContainer}>
          <Choose>
            <When condition={userHasNoMessages}>
              <Box mx={6} my={10}>
                <Box className={notificationsEmpty}>
                  <img
                    src={notificationZeroStateImage}
                    alt={t(COPY.NO_NOTIFICATIONS_IMG)}
                  />
                </Box>
                <Typography
                  variant="body"
                  align="center"
                  component="p"
                  color="dark"
                  weight="medium"
                >
                  {t(COPY.NO_NOTIFICATIONS_TITLE)}
                </Typography>
                <Typography
                  variant="bodySm"
                  align="center"
                  component="p"
                  color="darkMuted"
                >
                  {t(COPY.NO_NOTIFICATIONS_BODY)}{' '}
                  <Link
                    underline
                    href={`https://help.surveymonkey.com/${lang}/surveymonkey/send/survey-notifications/`}
                    target="_blank"
                  >
                    {t(COPY.LEARN)}
                  </Link>
                </Typography>
              </Box>
            </When>
            <When condition={!!data && !userHasNoMessages}>
              {showMarkAllAsRead && (
                <Box m={2}>
                  <Typography align="right">
                    <Button
                      size="sm"
                      color="muted"
                      onClick={clickMarkAllAsRead}
                      loading={loadingMarkAll}
                    >
                      {t(COPY.MARK_ALL)}
                    </Button>
                  </Typography>
                  {errorMarkAll && (
                    <Box mx={6} my={6}>
                      <Typography
                        variant="bodySm"
                        align="center"
                        component="p"
                        color="darkMuted"
                      >
                        {t(COPY.MARK_ALL_ERROR)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              <ul className={notificationsList}>
                {notificationsContent?.map(notification => (
                  <NotificationItem
                    key={`notification-id-${notification.id}`}
                    id={notification.id}
                    sentAt={notification.sentAt}
                    read={notification.isRead}
                    campaignName={notification.campaignName}
                  >
                    {notification.content}
                  </NotificationItem>
                ))}
              </ul>
              {!hasMoreToLoad && (
                <Box mx={8} my={10}>
                  <Typography
                    variant="bodySm"
                    align="center"
                    component="p"
                    color="darkMuted"
                  >
                    {t(COPY.END_OF_NOTIFICATIONS)}
                  </Typography>
                </Box>
              )}
              <InfiniteScrollTrigger
                hasMoreToLoad={hasMoreToLoad}
                onLoadMore={onLoadMore}
                loadingFirstData={loading}
                loadingMore={loading}
              />
              {hasMoreToLoad && <NotificationSkeletonLoader />}
            </When>
            <When condition={!!loading}>
              <NotificationSkeletonLoader />
            </When>
            <When condition={!!error}>
              <Box my={10}>
                <Typography
                  variant="bodySm"
                  align="center"
                  component="p"
                  color="darkMuted"
                >
                  {t(COPY.NOTIFICATIONS_ERROR)}
                </Typography>
              </Box>
            </When>
          </Choose>
        </Box>
      </Sheet.Body>
      {!userHasNoMessages && (
        <Sheet.Footer align="left">
          <Link
            underline
            href="#"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              onClickFeedback(e);
              window.open(
                `https://www.surveymonkey.com/r/5H7LWPQ?yuuzXf=%5B${userId}%5D&piDCatBf=%5B${packageId}%5D`,
                '_blank'
              );
              return false;
            }}
          >
            {t(COPY.SHARE)}
          </Link>
        </Sheet.Footer>
      )}
    </Sheet>
  );
};

export default NotificationsSheet;
export { NotificationsSheet };
